import React from 'react';
import { motion } from 'framer-motion';
import KineticHero from '../components/KineticHero';
import ManifestoSection from '../components/ManifestoSection';
import ServicesGrid from '../components/ServicesGrid';
import StatsSection from '../components/StatsSection';
import PortfolioSection from '../components/PortfolioSection';
import FeaturedServices from '../components/FeaturedServices';
import BlogSection from '../components/BlogSection';
import JsonLd from '../components/JsonLd';
import Marquee from '../components/Marquee';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const Home = () => {
  useSEO({
    title: 'Hei Web Creations | Premium Web Design & AI Workflows Singapore',
    description:
      'Hei Web Creations builds premium websites in Singapore — web design, WordPress, WooCommerce and portals — combining human expertise with AI-powered workflows.',
  });

  return (
    <div className="overflow-hidden bg-white" data-testid="home-page">
      <JsonLd
        id="org"
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Hei Web Creations Pte Ltd',
          url: typeof window !== 'undefined' ? window.location.origin : '',
          logo: typeof window !== 'undefined' ? `${window.location.origin}/logo.png` : '',
          description:
            'Premium web design company in Singapore combining human expertise with AI-powered workflows.',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '100 Peck Seah St, #07-07',
            postalCode: '079333',
            addressCountry: 'SG',
          },
          telephone: '+65 9888 3331',
          areaServed: 'Singapore',
        }}
      />
      {/* Kinetic Hero */}
      <KineticHero />

      {/* Marquee */}
      <Marquee />

      {/* Manifesto Section */}
      <ManifestoSection />

      {/* Stats Section */}
      <StatsSection />

      {/* Featured Services */}
      <FeaturedServices />

      {/* Services Preview */}
      <ServicesGrid showAll={false} />

      {/* Portfolio Section */}
      <PortfolioSection />

      {/* Why Choose Us Section */}
      <section className="py-32 bg-[#FAFAFA]" data-testid="why-choose-section">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
                Why choose us?
              </h2>
              <p className="text-lg md:text-xl text-gray-500 leading-relaxed mb-8">
                We're not just another web agency. We're your strategic partner in digital success, combining premium design with intelligent workflows to deliver exceptional results.
              </p>
              <Link to="/about">
                <Button
                  className="bg-[#1A1A1A] hover:bg-[#C8102E] text-white px-8 py-6 text-base rounded-full transition-all duration-300"
                  data-testid="learn-more-about-button"
                >
                  Learn More About Us
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Right Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              {[
                {
                  title: 'Premium Quality',
                  description: 'Agency-grade websites that rival the best in the industry',
                },
                {
                  title: 'Faster Delivery',
                  description: '50-88% faster turnaround without compromising quality',
                },
                {
                  title: 'Cost-Effective',
                  description: 'Premium results at competitive prices through AI efficiency',
                },
                {
                  title: 'Human Expertise',
                  description: 'Every project guided by experienced designers and strategists',
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#C8102E]/30 hover:shadow-lg transition-all duration-300"
                >
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">{item.title}</h3>
                  <p className="text-gray-500">{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      <BlogSection />

      {/* Final CTA Section */}
      <section className="py-32 bg-white" data-testid="final-cta-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto bg-[#1A1A1A] rounded-[2.5rem] px-8 py-20 md:px-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Ready to build something incredible?
            </h2>
            <p className="text-lg md:text-xl text-gray-400 mb-12 leading-relaxed max-w-2xl mx-auto">
              Let's discuss your project and explore how we can bring your vision to life with premium design and intelligent workflows.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    className="bg-[#C8102E] hover:bg-white hover:text-[#1A1A1A] text-white px-10 py-6 text-base rounded-full transition-all duration-300"
                    data-testid="final-cta-primary"
                  >
                    Start Your Project
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </motion.div>
              </Link>
              <Link to="/services">
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button
                    variant="ghost"
                    className="text-white hover:bg-white/10 px-10 py-6 text-base rounded-full transition-all duration-300"
                    data-testid="final-cta-secondary"
                  >
                    Explore Services
                  </Button>
                </motion.div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
